'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  PRODUCT_BADGE_PROFIT_VALUE_CLASS,
  PRODUCT_BADGE_TEXT_CLASS,
  PRODUCT_BADGE_WRAPPER_BASE,
} from './productBadgeStyles';

type ProductBadgesClientProps = {
  isPremium: boolean;
  isFree: boolean;
  discountPercent?: number | null;
  isBargain?: boolean;
  isUrgentSale?: boolean;
  collapsed?: boolean;
  className?: string;
};

type BadgeItem = {
  icon: ReactNode;
  text: ReactNode;
  wrapperClassName?: string;
  iconOnlyWrapperClassName?: string;
  textClassName?: string;
};

function isBadgeOverflowing(card: HTMLElement, container: HTMLElement, rtl: boolean): boolean {
  const cr = card.getBoundingClientRect();
  const tr = container.getBoundingClientRect();
  return rtl ? tr.left < cr.left + 8 : tr.right > cr.right - 8;
}

export const ProductBadgesClient = ({
  isPremium,
  isFree,
  discountPercent,
  isBargain,
  isUrgentSale,
  collapsed = false,
  className,
}: ProductBadgesClientProps) => {
  const t = useTranslations('product');
  const containerRef = useRef<HTMLDivElement>(null);
  const [collapsedCount, setCollapsedCount] = useState(0);

  const badges: BadgeItem[] = [];

  if (isPremium) {
    badges.push({
      icon: (
        <span className='inline-flex items-center justify-center w-[14px] h-[14px] text-[12px] leading-none'>💎</span>
      ),
      text: t('premium'),
    });
  }

  if (isFree) {
    badges.push({
      icon: <Image src='/assets/icons/for-free-mark.svg' width={12} height={12} alt='' />,
      text: t('for_free'),
    });
  }

  if (discountPercent) {
    badges.push({
      icon: <span className={PRODUCT_BADGE_PROFIT_VALUE_CLASS}>{discountPercent}%</span>,
      text: t('profit_badge_suffix'),
      wrapperClassName: 'pr-[4px]',
      iconOnlyWrapperClassName: 'border-0 bg-transparent p-0 h-auto',
    });
  }

  if (isBargain && isUrgentSale) {
    badges.push({
      icon: <Image src='/assets/icons/urgent-icon.svg' width={14} height={14} alt='' />,
      text: t('urgent_negotiable'),
    });
  } else {
    if (isBargain) {
      badges.push({
        icon: <Image src='/assets/icons/bargain-new.svg' width={14} height={14} alt='' />,
        text: t('negotiable'),
      });
    }
    if (isUrgentSale) {
      badges.push({
        icon: <Image src='/assets/icons/urgent-icon.svg' width={14} height={14} alt='' />,
        text: t('urgent_sale'),
      });
    }
  }

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const card = container.closest<HTMLElement>('[data-product-card-root]');
    if (!card) return;

    const rtl = document.documentElement.dir === 'rtl';
    let cardWidth = -1;

    const observer = new ResizeObserver((entries) => {
      const cardEntry = entries.find((entry) => entry.target === card);
      if (cardEntry && cardEntry.contentRect.width !== cardWidth) {
        const isResize = cardWidth !== -1;
        cardWidth = cardEntry.contentRect.width;
        if (isResize) {
          setCollapsedCount(0);
          return;
        }
      }

      if (isBadgeOverflowing(card, container, rtl)) {
        setCollapsedCount((prev) => Math.min(prev + 1, badges.length));
      }
    });

    observer.observe(card);
    observer.observe(container);
    return () => observer.disconnect();
  }, [badges.length]);

  if (badges.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className={twMerge('absolute top-[10px] start-[8px] flex flex-nowrap gap-[6px] z-2', className)}
    >
      {badges.map((badge, index) => {
        const isIconOnly = collapsed || index >= badges.length - collapsedCount;

        return (
          <div
            key={index}
            className={twMerge(
              PRODUCT_BADGE_WRAPPER_BASE,
              isIconOnly
                ? twMerge('pr-[2px]', badge.iconOnlyWrapperClassName)
                : twMerge('pr-[4px]', badge.wrapperClassName),
            )}
          >
            {badge.icon}
            {!isIconOnly && (
              <span className={twMerge(PRODUCT_BADGE_TEXT_CLASS, badge.textClassName)}>{badge.text}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};
